import {
  PrintServiceProductEntityHydrated,
  PrintServiceProductImageEntity,
} from "@jackfruit/common"
import { nanoid } from "@reduxjs/toolkit"
import { clamp, times } from "lodash"
import {
  PageType,
  ProductPageEntityHydrated,
} from "~/interfaces/entities/ProductPage"
import { getProductImageFitMode, getProductImageImageFitMode } from "./Utils"

interface Options {
  targetPageCount?: number
}

export const getPagesFromProduct = (
  product: PrintServiceProductEntityHydrated,
  options: Options = {}
): ProductPageEntityHydrated[] => {
  switch (product.categoryName) {
    // case "apparel":
    // case "badge":
    // case "blanket":
    // case "book":
    // case "calendar":
    // case "lenticular-card":
    // case "ornament":
    // case "tile":
    // case "print":
    // case "instax-style-print":
    // case "floating frame":
    // case "card":
    // case "canvas":
    // case "collage":
    // case "drinkware":
    // case "edible print":
    // case "flooring":
    // case "magnet":
    // case "metal":
    // case "mug":
    // case "other":
    // case "panel":
    // case "poster":
    // case "puzzle":
    // case "wallet":
    // case "wrapping-paper":
    case "photo-book":
      return getPagesFromPhotoBook(product, options)
    case "apparel":
      return getPagesFromOverlay(product)
    default:
      return getPagesFromDefault(product)
  }
}

const getPagesFromDefault = (
  product: PrintServiceProductEntityHydrated
): ProductPageEntityHydrated[] => {
  const minImages = product.minImages
  const wrap = product.metaData?.wrap || 0
  const width = product.pixelWidth + wrap * 2
  const height = product.pixelHeight + wrap * 2
  const imageFit = getProductImageFitMode(product)

  const pages: ProductPageEntityHydrated[] = times(minImages).map(_index => {
    return {
      id: nanoid(),
      pageType: PageType.Page,
      width,
      height,
      imageRegions: [
        {
          id: nanoid(),
          window: {
            x: 0,
            y: 0,
            width,
            height,
          },
          imageFitMode: imageFit,
        },
      ],
      textRegions: [],
    }
  })

  return pages
}

const getPagesFromOverlay = (
  product: PrintServiceProductEntityHydrated
): ProductPageEntityHydrated[] => {
  const imageFit = getProductImageFitMode(product)
  const portraitProductImage = product.printServiceProductImages!.find(
    productImage =>
      productImage.metaData?.frame?.width! <=
      productImage.metaData?.frame?.height!
  )

  const firstProductImage =
    portraitProductImage || product.printServiceProductImages![0]
  const innerFrameRegion = firstProductImage.metaData?.frame?.window

  const firstPage: ProductPageEntityHydrated = {
    id: nanoid(),
    pageType: PageType.Page,
    width: product.pixelWidth,
    height: product.pixelHeight,
    imageRegions: [
      {
        id: nanoid(),
        window: innerFrameRegion!,
        imageFitMode: imageFit,
      },
    ],
    textRegions: [],
  }
  return [firstPage]
}

const getPagesFromPhotoBook = (
  product: PrintServiceProductEntityHydrated,
  options: Options
) => {
  let imageFit = getProductImageFitMode(product)
  const { targetPageCount } = options

  // TODO add INP optimisation here (not available on current branch)
  const {
    printServiceProductImages = [],
    minImages,
    maxImages,
    pixelHeight,
    pixelWidth,
  } = product

  // Create Cover Page
  const frontCoverImage = printServiceProductImages.find(image => {
    return (
      image.metaData?.photoBook?.frameType === "windowCover" ||
      image.metaData?.photoBook?.frameType === "cover" ||
      image.metaData?.photoBook?.frameType === "customCover"
    )
  }) as PrintServiceProductImageEntity

  const hasWindowCover =
    frontCoverImage.metaData?.photoBook?.frameType === "windowCover"

  const hasCustomCover =
    frontCoverImage.metaData?.photoBook?.frameType === "customCover"

  const frontCoverPage: ProductPageEntityHydrated = {
    id: nanoid(),
    pageType: PageType.FrontCover,
    width: pixelWidth,
    height: pixelHeight,
    seeThrough: hasWindowCover,
    isCustomCover: hasCustomCover,
    imageRegions: [],
    textRegions: [],
  }

  if (hasCustomCover) {
    const imageImageFitMode = getProductImageImageFitMode(frontCoverImage)
    const imageFitToUse = imageImageFitMode || imageFit
    const { metaData: coverMetaData } = frontCoverImage

    const coverWidth = coverMetaData?.frame?.width || 0
    const coverHeight = coverMetaData?.frame?.height || 0

    frontCoverPage.width = coverWidth
    frontCoverPage.height = coverHeight

    const customCoverImageRegion = {
      id: nanoid(),
      window: {
        x: 0,
        y: 0,
        width: coverWidth,
        height: coverHeight,
      },
      imageFitMode: imageFitToUse,
    }

    frontCoverPage.imageRegions.push(customCoverImageRegion)
  }

  // Create Inside Cover Page
  const frontCoverInsideImage = printServiceProductImages.find(
    image => image.metaData?.photoBook?.frameType === "insideCover"
  ) as PrintServiceProductImageEntity

  const frontCoverInsidePage: ProductPageEntityHydrated = {
    id: nanoid(),
    pageType: PageType.FrontCoverInside,
    width: frontCoverInsideImage?.metaData?.frame?.width || pixelWidth,
    height: frontCoverInsideImage?.metaData?.frame?.height || pixelHeight,
    imageRegions: [],
    textRegions: [],
  }

  // custom cover books have one less page than the image count
  // this is due to the custom cover counting as one image
  const minPageCount =
    frontCoverImage.metaData.photoBook?.frameType === "customCover"
      ? minImages - 1
      : minImages

  const maxPageCount =
    frontCoverImage.metaData.photoBook?.frameType === "customCover"
      ? maxImages - 1
      : maxImages

  const currentTargetPageCount = targetPageCount ?? minPageCount

  const insidePagesCount = clamp(
    currentTargetPageCount,
    minPageCount,
    maxPageCount
  )

  const insidePages = times(insidePagesCount).map(_pageIndex => {
    return {
      id: nanoid(),
      pageType: PageType.Page,
      width: pixelWidth,
      height: pixelHeight,
      imageRegions: [
        {
          id: nanoid(),
          window: {
            x: 0,
            y: 0,
            width: pixelWidth,
            height: pixelHeight,
          },
          imageFitMode: imageFit,
        },
      ],
      textRegions: [],
    }
  })

  // Create Back Cover Inside
  const backCoverInsideImage = printServiceProductImages.find(
    image => image.metaData?.photoBook?.frameType === "lastPage"
  ) as PrintServiceProductImageEntity

  const backCoverInsidePage: ProductPageEntityHydrated = {
    id: nanoid(),
    pageType: PageType.BackCoverInside,
    width: backCoverInsideImage?.metaData?.frame?.width || pixelWidth,
    height: backCoverInsideImage?.metaData?.frame?.height || pixelHeight,
    imageRegions: [],
    textRegions: [],
  }

  const allBookPages = [
    frontCoverPage,
    frontCoverInsidePage,
    ...insidePages,
    backCoverInsidePage,
  ]

  return allBookPages
}
